import { useState, Fragment, useMemo, useEffect } from 'react';
import {
  Plus,
  Search,
  ChevronDown,
  ChevronRight,
  Building2,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  X,
  Users,
  MoreVertical,
  Pencil,
  Trash2,
  Star,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Info,
  Pause,
  Play,
  Landmark,
  Lock,
  Repeat,
  SearchX,
  Send,
} from 'lucide-react';
import { useLocationStore } from '@/stores/location-store';
import { PageHeader } from '@/components/shared/page-header';
import { EmptyState } from '@/components/shared/empty-state';
import { LocationBadge } from '@/components/shared/location-badge';
import { StatCard } from '@/components/shared/stat-card';
import { StatusBadge } from '@/components/shared/status-badge';
import {
  useSPPayees,
  useSPFundingAccounts,
  useLockedFundingAccount,
  useSPPayments,
  useSPDashboard,
  useCreateSPPayee,
  useUpdateSPPayee,
  useDeleteSPPayee,
  useCreateSPPayment,
  type SPPayee,
  type SPPayment,
} from '@/hooks/use-send-payments';
import {
  useRecurringPayments,
  useCreateRecurringPayment,
  useUpdateRecurringPayment,
  usePauseRecurringPayment,
  useResumeRecurringPayment,
  useCancelRecurringPayment,
  RECURRING_PAYMENT_FREQUENCIES,
  RECURRING_PAYMENT_FREQUENCY_LABELS,
  type RecurringPayment,
  type RecurringPaymentFrequency,
} from '@/hooks/use-recurring-payments';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { DateInput } from '@/components/ui/date-input';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { formatCurrency, formatDateTime, maskAccountNumber } from '@/lib/utils';
import { toast } from '@/stores/toast-store';
import { useCanOpenReports } from '@/hooks/use-reports-access';
import { PhoneInput } from '@/components/ui/phone-input';
import { validatePhone } from '@/lib/validators';
import { useVendorPurchaseOrders } from '@/hooks/use-purchase-orders';
import { CreateInvoiceTab, BillsTab } from '@/pages/dashboard/invoice-processor';
import { useVendorInvoiceBills } from '@/hooks/use-vendor-invoices';
import { useModules } from '@/hooks/use-modules';
import { useLocationsWithExternalId } from '@/hooks/use-locations';
import { apiErrorMessage } from '@/lib/api-error';
import { usePersistentTab } from '@/hooks/use-persistent-tab';
import { usePermissions } from '@/hooks/use-permissions';
import type { PermissionKey } from '@supernova/shared';

// ── Tab types ──
type Tab = 'send-payment' | 'recurring' | 'payments' | 'payees' | 'funding' | 'purchase-orders' | 'create-invoice';
const VALID_TABS: readonly Tab[] = [
  'send-payment',
  'recurring',
  'payments',
  'payees',
  'funding',
  'purchase-orders',
  'create-invoice',
];

// Which keys open each tab (any one of them). Funding Accounts is read-only
// here; reading the account needs Send a payment or the Funding Summary.
const TAB_KEYS: Partial<Record<Tab, readonly PermissionKey[]>> = {
  'send-payment': ['sp.payments.send', 'sp.recurring.create'],
  funding: ['sp.payments.send', 'rp.funding.view'],
  recurring: ['sp.recurring.view'],
  payees: ['sp.payees.view'],
  'create-invoice': ['sp.invoices.view'],
  'purchase-orders': ['sp.bills.view'],
  payments: ['sp.transactions.view'],
};

// ── Main Payables Page ──

export function PayablesPage() {
  const [activeTab, setActiveTab] = usePersistentTab<Tab>(VALID_TABS, 'send-payment');
  const [showNewPayee, setShowNewPayee] = useState(false);

  const { selectedLocationId } = useLocationStore();
  const { data: dashboardData, isLoading: dashLoading } = useSPDashboard({
    locationId: selectedLocationId || undefined,
  });
  const dash = dashboardData?.data;

  const { data: modulesData } = useModules(selectedLocationId || undefined);
  const invoiceModule = modulesData?.data?.find((m) => m.moduleName === 'invoice_processor');
  // Invoice processor only needs the super-admin gate — the business doesn't
  // need to separately opt-in via isActive (there's no settings toggle for it).
  const invoiceEnabled = invoiceModule?.superAdminEnabled;

  // Each tab shows only with its key; a saved tab the user can't open moves
  // to the first one they can.
  const perms = usePermissions();
  const permitsTab = (key: Tab) => {
    const needed = TAB_KEYS[key];
    return !needed || perms.hasAny(...needed);
  };

  const tabs: { key: Tab; label: string }[] = (
    [
      { key: 'send-payment', label: 'Send Payments' },
      { key: 'recurring', label: 'Recurring/Scheduled' },
      { key: 'payees', label: 'Payees / Vendors' },
      { key: 'funding', label: 'Funding Accounts' },
      ...(invoiceEnabled ? [{ key: 'create-invoice' as Tab, label: 'Vendor Invoice' }] : []),
      { key: 'purchase-orders', label: 'Bills' },
      { key: 'payments', label: 'Transactions' },
    ] as { key: Tab; label: string }[]
  ).filter((t) => permitsTab(t.key));

  const shownTab = permitsTab(activeTab) ? activeTab : null;
  const firstTab = tabs[0]?.key;
  useEffect(() => {
    if (!permitsTab(activeTab) && firstTab) setActiveTab(firstTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perms, activeTab, firstTab, setActiveTab]);

  return (
    <div className="min-w-0 space-y-6">
      <PageHeader title="Send Payments" description="Manage vendor payments and accounts payable">
        <LocationBadge />
      </PageHeader>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Active Payees"
          value={dashLoading ? '...' : String(dash?.payees.active ?? 0)}
          subtitle={`${dash?.payees.total ?? 0} total payees`}
        />
        <StatCard
          title="Total Payments"
          value={dashLoading ? '...' : String(dash?.payments.total ?? 0)}
          subtitle="All transactions"
        />
        <StatCard
          title="Pending"
          value={dashLoading ? '...' : String(dash?.payments.pending ?? 0)}
          subtitle="Awaiting processing"
        />
        <StatCard
          title="Total Paid"
          value={dashLoading ? '...' : formatCurrency(dash?.payments.completedAmount ?? '0')}
          changeType="positive"
          subtitle="Completed payments"
        />
      </div>

      {/* Tab bar */}
      <div className="border-b border-border overflow-x-auto scrollbar-hide">
        <div className="flex w-full min-w-0">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative shrink-0 px-4 py-2.5 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap text-center flex items-center gap-2 ${
                activeTab === tab.key ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              {activeTab === tab.key && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {shownTab === 'send-payment' && <SendPaymentTab />}
      {shownTab === 'recurring' && <RecurringPaymentsTab />}
      {shownTab === 'payments' && <PaymentsTab />}
      {shownTab === 'payees' && <PayeesTab onAddPayee={() => setShowNewPayee(true)} />}
      {shownTab === 'funding' && <FundingAccountsTab />}
      {shownTab === 'create-invoice' && <CreateInvoiceTab />}
      {shownTab === 'purchase-orders' && <BillsTab />}

      {/* Modals */}
      {showNewPayee && <NewPayeeModal onClose={() => setShowNewPayee(false)} />}
    </div>
  );
}

// ── Send Payment Tab (inline form) ──

function SendPaymentTab() {
  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const createPayment = useCreateSPPayment();
  const createRecurringPayment = useCreateRecurringPayment();
  const { selectedLocationId } = useLocationStore();
  // Sending now and scheduling are separate keys; with only one of them the
  // form offers just that.
  const perms = usePermissions();
  const canSendNow = perms.has('sp.payments.send');
  const canSchedule = perms.has('sp.recurring.create');
  const canSeeBills = perms.has('sp.bills.view');

  // Fetch locations with externalId (MID) so we can pass merchantId to the
  // payment create request — the backend requires it to look up the gateway config.
  const { data: locationsData } = useLocationsWithExternalId();
  const selectedLocation = (locationsData?.data ?? []).find((l: any) => l.id === selectedLocationId);
  const merchantId: string | undefined = selectedLocation?.externalId ?? undefined;

  const { data: payeeData } = useSPPayees();
  // Locked to the location's primary funding account (from onboarding) — not user-selectable.
  const { account: lockedAccount, isLoading: lockedAccountLoading } = useLockedFundingAccount();

  const payees = (payeeData?.data ?? []).filter((p: SPPayee) => p.isActive);

  const [payeeId, setPayeeId] = useState('');
  const [amount, setAmount] = useState('');
  // Statement / Transaction Descriptor: required, user-entered, max 10 chars.
  const [statementSuffix, setStatementSuffix] = useState('');
  const [descriptorTouched, setDescriptorTouched] = useState(false);
  const [memo, setMemo] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('ach');

  // Recurring / Schedule for Later — mutually exclusive with each other.
  const [recurringEnabled, setRecurringEnabled] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<RecurringPaymentFrequency>('monthly');
  const [recurringStartDate, setRecurringStartDate] = useState('');
  const [recurringStartTime, setRecurringStartTime] = useState('09:00');
  const [recurringHasEnd, setRecurringHasEnd] = useState(false);
  const [recurringEndDate, setRecurringEndDate] = useState('');
  const [recurringError, setRecurringError] = useState('');
  const [scheduledEnabled, setScheduledEnabled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [scheduledError, setScheduledError] = useState('');

  const activeFundingAccountId = lockedAccount?.id ?? '';

  const activeStatementDescriptor = statementSuffix.trim();
  const descriptorError = !statementSuffix.trim() ? 'Statement / Transaction Descriptor is required' : '';

  const selectedPayee = useMemo(() => payees.find((p: SPPayee) => p.id === payeeId), [payees, payeeId]);
  const selectedAccount = lockedAccount;
  const isFormValid =
    payeeId &&
    activeFundingAccountId &&
    amount &&
    parseFloat(amount) > 0 &&
    !!statementSuffix.trim() &&
    (canSendNow || recurringEnabled || scheduledEnabled);

  const handleConfirm = async () => {
    if (!selectedLocationId) {
      toast.error('Select a location before sending a payment');
      return;
    }

    const basePayload = {
      payeeId,
      fundingAccountId: activeFundingAccountId,
      amount: parseFloat(amount),
      // Scope the payment to the active location so it's visible to the
      // location-filtered Transactions tab. Without this the row goes in
      // with locationId=NULL and falls out of the listing.
      locationId: selectedLocationId,
      // Pass the location's MID so the backend can look up the gateway config.
      merchantId,
      statementDescriptor: activeStatementDescriptor || undefined,
      memo: memo || undefined,
      paymentMethod,
    };

    try {
      if (recurringEnabled) {
        if (!recurringStartDate) {
          setRecurringError('Start date is required');
          return;
        }
        const startAtIso = new Date(`${recurringStartDate}T${recurringStartTime || '00:00'}:00`).toISOString();
        if (new Date(startAtIso) < new Date()) {
          setRecurringError('Start date cannot be in the past');
          return;
        }
        let endAtIso: string | null = null;
        if (recurringHasEnd) {
          if (!recurringEndDate) {
            setRecurringError('End date is required');
            return;
          }
          const endDate = new Date(`${recurringEndDate}T23:59:59`);
          if (endDate <= new Date(startAtIso)) {
            setRecurringError('End date must be after the start date');
            return;
          }
          endAtIso = endDate.toISOString();
        }
        setRecurringError('');
        await createRecurringPayment.mutateAsync({
          ...basePayload,
          frequency: recurringFrequency,
          startAt: startAtIso,
          endAt: endAtIso,
        });
        toast.success('Recurring payment scheduled! The first send will run on the start date.');
      } else if (scheduledEnabled) {
        if (!scheduledDate) {
          setScheduledError('Scheduled date is required');
          return;
        }
        const startAtIso = new Date(`${scheduledDate}T${scheduledTime || '00:00'}:00`).toISOString();
        if (new Date(startAtIso) <= new Date()) {
          setScheduledError('Scheduled date must be in the future');
          return;
        }
        setScheduledError('');
        await createRecurringPayment.mutateAsync({
          ...basePayload,
          frequency: 'once',
          startAt: startAtIso,
        });
        toast.success('Payment scheduled! It will be sent on the selected date.');
      } else {
        await createPayment.mutateAsync(basePayload);
        toast.success('Payment created successfully');
      }

      // Reset form
      setStep('form');
      setPayeeId('');
      setAmount('');
      setStatementSuffix('');
      setDescriptorTouched(false);
      setMemo('');
      setPaymentMethod('ach');
      setRecurringEnabled(false);
      setRecurringHasEnd(false);
      setRecurringEndDate('');
      setRecurringStartDate('');
      setScheduledEnabled(false);
      setScheduledDate('');
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err, 'Failed to create payment'));
    }
  };

  const handleReset = () => {
    setPayeeId('');
    setAmount('');
    setStatementSuffix('');
    setDescriptorTouched(false);
    setMemo('');
    setPaymentMethod('ach');
    setRecurringEnabled(false);
    setRecurringHasEnd(false);
    setRecurringEndDate('');
    setRecurringStartDate('');
    setRecurringError('');
    setScheduledEnabled(false);
    setScheduledDate('');
    setScheduledError('');
    setStep('form');
  };

  if (step === 'confirm') {
    return (
      <div className="mx-auto max-w-2xl">
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-foreground mb-4">Confirm Payment</h3>

          <div className="flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-amber-700 mb-4">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="text-sm">Please review the payment details before confirming</span>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-1.5 border-b border-border/50">
              <span className="text-muted-foreground">Pay To:</span>
              <span className="font-medium">{selectedPayee?.name}</span>
            </div>
            {selectedPayee?.bankName && (
              <div className="flex justify-between py-1.5 border-b border-border/50">
                <span className="text-muted-foreground">Payee Bank:</span>
                <span>
                  {selectedPayee.bankName} &middot; {maskAccountNumber(selectedPayee.accountNumber)}
                </span>
              </div>
            )}
            <div className="flex justify-between py-1.5 border-b border-border/50">
              <span className="text-muted-foreground">From Account:</span>
              <span>
                {selectedAccount?.nickname} ({selectedAccount?.bankName})
              </span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/50">
              <span className="text-muted-foreground">Amount:</span>
              <span className="text-lg font-bold text-primary">{formatCurrency(parseFloat(amount))}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/50">
              <span className="text-muted-foreground">Method:</span>
              <span className="uppercase">{paymentMethod}</span>
            </div>
            {activeStatementDescriptor && (
              <div className="flex justify-between py-1.5 border-b border-border/50">
                <span className="text-muted-foreground">Statement Descriptor:</span>
                <span>{activeStatementDescriptor}</span>
              </div>
            )}
            {memo && (
              <div className="flex justify-between py-1.5 border-b border-border/50">
                <span className="text-muted-foreground">Memo:</span>
                <span>{memo}</span>
              </div>
            )}
            {recurringEnabled && (
              <div className="flex justify-between py-1.5">
                <span className="text-muted-foreground">Recurring:</span>
                <span>
                  {RECURRING_PAYMENT_FREQUENCY_LABELS[recurringFrequency]}, starting{' '}
                  {recurringStartDate && formatDateTime(new Date(`${recurringStartDate}T${recurringStartTime}`))}
                  {recurringHasEnd && recurringEndDate ? ` until ${recurringEndDate}` : ''}
                </span>
              </div>
            )}
            {scheduledEnabled && (
              <div className="flex justify-between py-1.5">
                <span className="text-muted-foreground">Scheduled For:</span>
                <span>{scheduledDate && formatDateTime(new Date(`${scheduledDate}T${scheduledTime}`))}</span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-4">
            <button
              onClick={() => setStep('form')}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <button
              onClick={handleConfirm}
              disabled={createPayment.isPending || createRecurringPayment.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-5 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              <CheckCircle2 className="h-4 w-4" />
              {createPayment.isPending || createRecurringPayment.isPending ? 'Processing...' : 'Confirm Payment'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-foreground mb-4">New Payment</h3>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setStep('confirm');
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-foreground">
              From Funding Account <span className="text-red-500">*</span>
            </label>
            {lockedAccount ? (
              <div
                aria-readonly="true"
                title="Set from onboarding bank details and can't be changed"
                className="mt-1 flex h-9 w-full cursor-not-allowed items-center gap-2 rounded-lg border border-input bg-muted/50 px-3 text-sm text-foreground"
              >
                <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">
                  {lockedAccount.nickname} ({lockedAccount.bankName} - {maskAccountNumber(lockedAccount.accountNumber)})
                </span>
              </div>
            ) : (
              <div className="mt-1 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {lockedAccountLoading
                  ? 'Loading funding account...'
                  : !selectedLocationId
                    ? 'Select a location to see its funding account.'
                    : 'No funding account is set up for this location. Contact support to add your onboarding bank account.'}
              </div>
            )}
            <p className="mt-1 text-xs text-muted-foreground">Payments are sent from your onboarding bank account.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground">
              To Payee <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              className="mt-1"
              value={payeeId}
              onChange={setPayeeId}
              options={payees.map((p: SPPayee) => ({
                value: p.id,
                label: p.company ? `${p.name} (${p.company})` : p.name,
              }))}
              placeholder="Select Payee"
              searchPlaceholder="Search payees..."
              emptyMessage="No payees found"
            />
            {selectedPayee?.bankName && (
              <div className="mt-1.5 flex items-center gap-1.5 rounded-md bg-muted/50 px-2.5 py-1.5 text-xs text-muted-foreground">
                <Building2 className="h-3 w-3" />
                {selectedPayee.bankName} &middot; {maskAccountNumber(selectedPayee.accountNumber)}
              </div>
            )}
            {payeeId && canSeeBills && (
              <BillLinkDropdown
                payeeId={payeeId}
                onSelect={(amt, inv) => {
                  setAmount(String(amt));
                  setMemo(`Payment for Invoice #${inv}`);
                }}
              />
            )}
          </div>

          {!canSendNow && (
            <p className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
              You can schedule payments but not send one now. Choose a recurring or future date below.
            </p>
          )}

          {canSchedule && (
            <>
              <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={recurringEnabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setRecurringEnabled(checked);
                      setRecurringError('');
                      if (checked) {
                        setScheduledEnabled(false);
                        setScheduledError('');
                        if (!recurringStartDate) {
                          const d = new Date();
                          setRecurringStartDate(
                            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
                          );
                        }
                      }
                    }}
                    className="mt-0.5 h-4 w-4 accent-primary"
                  />
                  <span className="text-sm">
                    <span className="font-medium text-foreground">Make this a recurring payment</span>
                    <span className="block text-xs text-muted-foreground">
                      Schedule this payment to repeat automatically on the cadence you choose.
                    </span>
                  </span>
                </label>

                {recurringEnabled && (
                  <div className="space-y-3 rounded-lg border border-border bg-white p-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-foreground">
                        Frequency <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={recurringFrequency}
                        onChange={(e) => setRecurringFrequency(e.target.value as RecurringPaymentFrequency)}
                        className="h-9 w-full rounded-lg border border-input px-3 text-sm"
                      >
                        {RECURRING_PAYMENT_FREQUENCIES.map((freq) => (
                          <option key={freq} value={freq}>
                            {RECURRING_PAYMENT_FREQUENCY_LABELS[freq]}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-foreground">
                          Start Date <span className="text-red-500">*</span>
                        </label>
                        <DateInput
                          value={recurringStartDate}
                          min={(() => {
                            const d = new Date();
                            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                          })()}
                          onChange={(e) => setRecurringStartDate(e.target.value)}
                          className="h-9 w-full rounded-lg border border-input px-3 text-sm"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-foreground">
                          Start Time <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="time"
                          value={recurringStartTime}
                          onChange={(e) => setRecurringStartTime(e.target.value)}
                          className="h-9 w-full rounded-lg border border-input px-3 text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-foreground">End</label>
                      <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                          <input
                            type="radio"
                            name="recurringEnd"
                            checked={!recurringHasEnd}
                            onChange={() => setRecurringHasEnd(false)}
                            className="h-4 w-4 accent-primary"
                          />
                          <span className="text-sm">No end date</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                          <input
                            type="radio"
                            name="recurringEnd"
                            checked={recurringHasEnd}
                            onChange={() => setRecurringHasEnd(true)}
                            className="h-4 w-4 accent-primary"
                          />
                          <span className="text-sm">End on</span>
                        </label>
                      </div>
                      {recurringHasEnd && (
                        <DateInput
                          value={recurringEndDate}
                          min={recurringStartDate || undefined}
                          onChange={(e) => setRecurringEndDate(e.target.value)}
                          className="mt-2 h-9 w-full rounded-lg border border-input px-3 text-sm"
                        />
                      )}
                    </div>

                    {recurringError && (
                      <p className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700" role="alert">
                        {recurringError}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scheduledEnabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setScheduledEnabled(checked);
                      setScheduledError('');
                      if (checked) {
                        setRecurringEnabled(false);
                        setRecurringError('');
                      }
                    }}
                    className="mt-0.5 h-4 w-4 accent-primary"
                  />
                  <span className="text-sm">
                    <span className="font-medium text-foreground">Schedule one time payment for future date</span>
                    <span className="block text-xs text-muted-foreground">
                      Process this as a one-time payment on the date you choose.
                    </span>
                  </span>
                </label>

                {scheduledEnabled && (
                  <div className="grid grid-cols-2 gap-3 rounded-lg border border-border bg-white p-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-foreground">
                        Scheduled Date <span className="text-red-500">*</span>
                      </label>
                      <DateInput
                        value={scheduledDate}
                        min={(() => {
                          const d = new Date();
                          d.setDate(d.getDate() + 1);
                          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                        })()}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        className="h-9 w-full rounded-lg border border-input px-3 text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-foreground">
                        Scheduled Time <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        className="h-9 w-full rounded-lg border border-input px-3 text-sm"
                      />
                    </div>
                    {scheduledError && (
                      <p
                        className="col-span-2 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700"
                        role="alert"
                      >
                        {scheduledError}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground">
                Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                <input
                  type="number"
                  required
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="h-9 w-full rounded-lg border border-input pl-7 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground">Method</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="mt-1 h-9 w-full rounded-lg border border-input px-3 text-sm"
              >
                <option value="ach">ACH</option>
                <option value="wire">Wire Transfer</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground">
              Statement / Transaction Descriptor <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={statementSuffix}
              onChange={(e) => setStatementSuffix(e.target.value)}
              onBlur={() => setDescriptorTouched(true)}
              maxLength={10}
              placeholder="Up to 10 characters"
              aria-invalid={!!(descriptorTouched && descriptorError)}
              className="mt-1 h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <p className="mt-1 text-xs text-muted-foreground">Enter your bill, invoice number, etc.</p>
            {descriptorTouched && descriptorError && <p className="mt-1 text-xs text-red-500">{descriptorError}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground">
              Memo <span className="text-xs text-muted-foreground">(optional)</span>
            </label>
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              rows={2}
              placeholder="Payment Description..."
              className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleReset}
              className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={!isFormValid}
              className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              Review Payment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Recurring / Scheduled Payments Tab ──

function RecurringPaymentsTab() {
  const { selectedLocationId } = useLocationStore();
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'paused' | 'cancelled' | 'completed'>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const { data, isLoading } = useRecurringPayments({
    locationId: selectedLocationId || undefined,
    status: statusFilter || undefined,
  });
  const { data: payeeData } = useSPPayees();
  const payeeLookup = useMemo(() => {
    const map = new Map<string, SPPayee>();
    (payeeData?.data ?? []).forEach((p: SPPayee) => map.set(p.id, p));
    return map;
  }, [payeeData]);

  const pauseRule = usePauseRecurringPayment();
  const resumeRule = useResumeRecurringPayment();
  const cancelRule = useCancelRecurringPayment();
  const perms = usePermissions();
  const canEditRule = perms.has('sp.recurring.edit');
  const canCancelRule = perms.has('sp.recurring.cancel');

  const [editing, setEditing] = useState<RecurringPayment | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<RecurringPayment | null>(null);

  const filteredRules = useMemo(() => {
    const rules = data?.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return rules;
    return rules.filter((rule) => (payeeLookup.get(rule.payeeId)?.name || '').toLowerCase().includes(q));
  }, [data, payeeLookup, search]);

  const total = filteredRules.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(
    () => filteredRules.slice((page - 1) * perPage, page * perPage),
    [filteredRules, page, perPage],
  );

  const togglePauseResume = async (rule: RecurringPayment) => {
    try {
      if (rule.status === 'active') {
        await pauseRule.mutateAsync(rule.id);
        toast.success('Recurring payment paused');
      } else {
        await resumeRule.mutateAsync(rule.id);
        toast.success('Recurring payment resumed');
      }
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err, 'Failed to update recurring payment'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">Recurring / Scheduled</h3>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by payee name..."
              className="h-9 w-64 rounded-lg border border-input bg-white pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as typeof statusFilter);
              setPage(1);
            }}
            className="h-9 rounded-lg border border-input bg-white px-3 text-sm"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">ID</th>
              <th className="px-4 py-3 font-medium">Payee</th>
              <th className="px-4 py-3 font-medium">Amount</th>
              <th className="px-4 py-3 font-medium">Method</th>
              <th className="px-4 py-3 font-medium">Frequency</th>
              <th className="px-4 py-3 font-medium">Next Run</th>
              <th className="px-4 py-3 font-medium">End</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                  Loading...
                </td>
              </tr>
            ) : total === 0 ? (
              <tr>
                <td colSpan={9}>
                  {search.trim() ? (
                    <EmptyState
                      icon={SearchX}
                      title="No matching recurring or scheduled payments"
                      description="Try a different search or clear it."
                    />
                  ) : (
                    <EmptyState
                      icon={Repeat}
                      title="No recurring or scheduled payments yet"
                      description="Enable one from the Send Payments tab."
                    />
                  )}
                </td>
              </tr>
            ) : (
              pageRows.map((rule) => {
                const payee = payeeLookup.get(rule.payeeId);
                const isEditable = rule.status === 'active' || rule.status === 'paused';
                return (
                  <tr key={rule.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-muted-foreground" title={rule.id}>
                        {rule.id.slice(0, 8)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{payee?.name || '—'}</div>
                      {payee?.company && <div className="text-xs text-muted-foreground">{payee.company}</div>}
                    </td>
                    <td className="px-4 py-3 font-medium">{formatCurrency(rule.amount)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Landmark className="h-4 w-4 text-muted-foreground" />
                        <span className="uppercase">{rule.paymentMethod}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {RECURRING_PAYMENT_FREQUENCY_LABELS[rule.frequency] ?? rule.frequency}
                    </td>
                    <td className="px-4 py-3">{formatDateTime(rule.nextRunAt)}</td>
                    <td className="px-4 py-3">{rule.endAt ? formatDateTime(rule.endAt) : '—'}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={rule.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {isEditable && canEditRule && (
                          <>
                            <button
                              type="button"
                              onClick={() => togglePauseResume(rule)}
                              disabled={pauseRule.isPending || resumeRule.isPending}
                              className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs font-medium hover:bg-muted disabled:opacity-50"
                              title={rule.status === 'active' ? 'Pause' : 'Resume'}
                            >
                              {rule.status === 'active' ? (
                                <>
                                  <Pause className="h-3.5 w-3.5" /> Pause
                                </>
                              ) : (
                                <>
                                  <Play className="h-3.5 w-3.5" /> Resume
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditing(rule)}
                              className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs font-medium hover:bg-muted"
                              title="Edit schedule"
                            >
                              <Pencil className="h-3.5 w-3.5" /> Edit
                            </button>
                          </>
                        )}
                        {isEditable && canCancelRule && (
                          <button
                            type="button"
                            onClick={() => setConfirmCancel(rule)}
                            className="inline-flex h-8 items-center gap-1 rounded-md border border-red-200 px-2 text-xs font-medium text-red-600 hover:bg-red-50"
                            title="Cancel"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <DataTablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          perPageOptions={[10, 25, 50, 100]}
          onPageChange={setPage}
          onPerPageChange={(n) => {
            setPerPage(n);
            setPage(1);
          }}
        />
      )}

      {editing && <EditRecurringPaymentModal rule={editing} onClose={() => setEditing(null)} />}

      <ConfirmDialog
        open={!!confirmCancel}
        title="Cancel recurring payment?"
        message="This will stop all future occurrences. Payments already sent are not affected."
        confirmLabel="Cancel Recurring Payment"
        variant="danger"
        onConfirm={async () => {
          if (!confirmCancel) return;
          try {
            await cancelRule.mutateAsync(confirmCancel.id);
            toast.success('Recurring payment cancelled');
          } catch (err: unknown) {
            toast.error(apiErrorMessage(err, 'Failed to cancel recurring payment'));
          }
          setConfirmCancel(null);
        }}
        onCancel={() => setConfirmCancel(null)}
      />
    </div>
  );
}

function toDateInputValue(iso: string): string {
  return new Date(iso).toISOString().split('T')[0];
}

function toTimeInputValue(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function EditRecurringPaymentModal({ rule, onClose }: { rule: RecurringPayment; onClose: () => void }) {
  const updateRule = useUpdateRecurringPayment();
  const isOneTime = rule.frequency === 'once';
  const [frequency, setFrequency] = useState<RecurringPaymentFrequency>(
    RECURRING_PAYMENT_FREQUENCIES.includes(rule.frequency as (typeof RECURRING_PAYMENT_FREQUENCIES)[number])
      ? rule.frequency
      : 'monthly',
  );
  const [nextRunDate, setNextRunDate] = useState(toDateInputValue(rule.nextRunAt));
  const [nextRunTime, setNextRunTime] = useState(toTimeInputValue(rule.nextRunAt));
  const [hasEnd, setHasEnd] = useState(!!rule.endAt);
  const [endDate, setEndDate] = useState(rule.endAt ? toDateInputValue(rule.endAt) : '');
  const [amount, setAmount] = useState(rule.amount);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!nextRunDate) {
      setError('Next run date is required');
      return;
    }
    const nextRunIso = new Date(`${nextRunDate}T${nextRunTime || '00:00'}:00`).toISOString();
    let endAtIso: string | null = null;
    if (hasEnd) {
      if (!endDate) {
        setError('End date is required');
        return;
      }
      const endParsed = new Date(`${endDate}T23:59:59`);
      if (endParsed <= new Date(nextRunIso)) {
        setError('End date must be after the next run');
        return;
      }
      endAtIso = endParsed.toISOString();
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError('Amount must be positive');
      return;
    }
    setError('');
    try {
      await updateRule.mutateAsync({
        id: rule.id,
        data: {
          amount: parseFloat(amount),
          ...(isOneTime ? {} : { frequency, nextRunAt: nextRunIso, endAt: endAtIso }),
        },
      });
      toast.success('Recurring payment updated');
      onClose();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Failed to update recurring payment'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="text-sm font-semibold">Edit {isOneTime ? 'scheduled payment' : 'recurring schedule'}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 px-5 py-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-10 w-full rounded-lg border border-input bg-white pl-7 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          {!isOneTime && (
            <div>
              <label className="mb-1 block text-sm font-medium">Frequency</label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as RecurringPaymentFrequency)}
                className="h-10 w-full rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              >
                {RECURRING_PAYMENT_FREQUENCIES.map((freq) => (
                  <option key={freq} value={freq}>
                    {RECURRING_PAYMENT_FREQUENCY_LABELS[freq]}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">{isOneTime ? 'Scheduled date' : 'Next run date'}</label>
              <DateInput
                value={nextRunDate}
                onChange={(e) => setNextRunDate(e.target.value)}
                className="h-10 w-full rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{isOneTime ? 'Scheduled time' : 'Next run time'}</label>
              <input
                type="time"
                value={nextRunTime}
                onChange={(e) => setNextRunTime(e.target.value)}
                className="h-10 w-full rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          {!isOneTime && (
            <div>
              <label className="mb-2 block text-sm font-medium">End</label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                  <input
                    type="radio"
                    name="editEnd"
                    checked={!hasEnd}
                    onChange={() => setHasEnd(false)}
                    className="h-4 w-4 accent-primary"
                  />
                  <span className="text-sm">No end date</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                  <input
                    type="radio"
                    name="editEnd"
                    checked={hasEnd}
                    onChange={() => setHasEnd(true)}
                    className="h-4 w-4 accent-primary"
                  />
                  <span className="text-sm">End on</span>
                </label>
              </div>
              {hasEnd && (
                <DateInput
                  value={endDate}
                  min={nextRunDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-2 h-10 w-full rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              )}
            </div>
          )}

          {error && (
            <p className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700" role="alert">
              {error}
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-border px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={updateRule.isPending}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {updateRule.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Payments Tab ──

type SPSortField = 'date' | 'amount' | 'payeeName' | 'status';
type SPSortDirection = 'asc' | 'desc';

function SPSortableHeader({
  label,
  field,
  sortField,
  sortDirection,
  onSort,
  align = 'left',
}: {
  label: string;
  field: SPSortField;
  sortField: SPSortField;
  sortDirection: SPSortDirection;
  onSort: (field: SPSortField) => void;
  align?: 'left' | 'right';
}) {
  const isActive = sortField === field;
  return (
    <th
      className={`px-3 py-3 text-xs font-medium text-muted-foreground whitespace-nowrap cursor-pointer select-none hover:text-foreground transition-colors ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => onSort(field)}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        {label}
        {isActive ? (
          sortDirection === 'asc' ? (
            <ArrowUp className="h-3 w-3 text-primary" />
          ) : (
            <ArrowDown className="h-3 w-3 text-primary" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </span>
    </th>
  );
}

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

function PaymentsTab() {
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SPSortField>('date');
  const [sortDirection, setSortDirection] = useState<SPSortDirection>('desc');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const { selectedLocationId } = useLocationStore();

  const { data, isLoading } = useSPPayments({
    status: statusFilter || undefined,
    locationId: selectedLocationId || undefined,
  });

  const payments = data?.data ?? [];

  // Captured once at mount; full history lives under Reports
  const [nowMs] = useState(() => Date.now());

  // Only offer the link to people it would open for: Reports switched on and
  // the Send Payments reports key held. Resubmitting sends money again.
  const perms = usePermissions();
  const canOpenReports = useCanOpenReports() && perms.has('reports.sp.view');
  const canResubmit = perms.has('sp.payments.send');
  const openTransactionsHistory = () => {
    window.location.hash = '/reports?tab=send&report=transactions-history';
  };

  // Filter by date range and search query
  const filteredPayments = useMemo(() => {
    // Only show transactions from the last 30 days
    const cutoff = nowMs - 30 * 24 * 60 * 60 * 1000;
    let result = payments.filter((p) => {
      const time = new Date(p.createdAt).getTime();
      return Number.isNaN(time) || time >= cutoff;
    });

    if (search.trim()) {
      const query = search.toLowerCase();
      result = result.filter(
        (p) =>
          (p.payee?.name || '').toLowerCase().includes(query) ||
          (p.payee?.company || '').toLowerCase().includes(query) ||
          (p.referenceNumber || '').toLowerCase().includes(query) ||
          (p.fundingAccount?.nickname || '').toLowerCase().includes(query),
      );
    }

    return result;
  }, [payments, search, nowMs]);

  // Sort filtered results
  const sortedPayments = useMemo(() => {
    const sorted = [...filteredPayments];
    sorted.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'date':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'amount':
          comparison = parseFloat(String(a.amount)) - parseFloat(String(b.amount));
          break;
        case 'payeeName':
          comparison = (a.payee?.name || '').localeCompare(b.payee?.name || '');
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [filteredPayments, sortField, sortDirection]);

  const total = sortedPayments.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(
    () => sortedPayments.slice((page - 1) * perPage, page * perPage),
    [sortedPayments, page, perPage],
  );

  const handleSort = (field: SPSortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="min-w-0 space-y-4">
      {canOpenReports ? (
        <button
          type="button"
          onClick={openTransactionsHistory}
          className="group flex w-full items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted/70"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p className="flex-1">
            Showing transactions from the last 30 days. For older transactions and full history, see the{' '}
            <span className="font-medium text-primary group-hover:underline">Reports</span> section.
          </p>
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary" />
        </button>
      ) : (
        <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p>
            Showing transactions from the last 30 days. For older transactions and full history, see the{' '}
            <span className="font-medium text-foreground">Reports</span> section.
          </p>
        </div>
      )}
      <div className="flex items-center gap-1.5 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-amber-700">
        <AlertCircle className="h-3 w-3 shrink-0" />
        <span className="text-[11px] sm:text-xs">9 PM EST cut-off time for next-day funding</span>
      </div>
      <div className="flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
        >
          <option value="">All Statuses</option>
          <option value="sent">Sent</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="submitted">Submitted</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
          <option value="returned">Returned</option>
        </select>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by payee, reference, or account..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading payments...</div>
      ) : sortedPayments.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-border">
          {search || statusFilter ? (
            <EmptyState
              icon={SearchX}
              title="No matching payments"
              description={search ? 'Try a different search term or clear the filters.' : undefined}
            />
          ) : (
            <EmptyState icon={Send} title="No payments yet" description="Create your first payment to get started." />
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-white">
          <table className="min-w-[800px] w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="w-10 px-3 py-3"></th>
                <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">Reference</th>
                <SPSortableHeader
                  label="Payee"
                  field="payeeName"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <SPSortableHeader
                  label="Amount"
                  field="amount"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">Method</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">From Account</th>
                <SPSortableHeader
                  label="Status"
                  field="status"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <SPSortableHeader
                  label="Date"
                  field="date"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((payment) => (
                <Fragment key={payment.id}>
                  <tr
                    className="border-b border-border hover:bg-muted/30 cursor-pointer"
                    onClick={() => toggleRow(payment.id)}
                  >
                    <td className="px-3 py-3">
                      <button className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted">
                        {expandedRows.has(payment.id) ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-mono text-xs font-medium text-primary">{payment.referenceNumber}</span>
                    </td>
                    <td className="px-3 py-3 text-sm">
                      {payment.payee?.name || 'Unknown'}
                      {payment.payee?.company && (
                        <span className="ml-1 text-xs text-muted-foreground">({payment.payee.company})</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-sm font-medium text-right">{formatCurrency(payment.amount)}</td>
                    <td className="px-3 py-3">
                      <span className="uppercase text-xs font-medium">{payment.paymentMethod}</span>
                    </td>
                    <td className="px-3 py-3 text-sm">{payment.fundingAccount?.nickname || 'N/A'}</td>
                    <td className="px-3 py-3">
                      <StatusBadge status={payment.status} labelMap={{ processing: 'Processed' }} />
                    </td>
                    <td className="px-3 py-3 text-sm text-muted-foreground whitespace-nowrap">
                      {formatDateTime(payment.createdAt)}
                    </td>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      {canResubmit &&
                        payment.status === 'failed' &&
                        (payment.statusMessage?.includes('R01') || payment.statusMessage?.includes('R09')) && (
                          <button
                            onClick={() => {
                              /* TODO: wire up resubmit API */
                            }}
                            className="rounded-md bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive hover:bg-destructive/20 transition-colors whitespace-nowrap"
                          >
                            Resubmit
                          </button>
                        )}
                    </td>
                  </tr>
                  {expandedRows.has(payment.id) && (
                    <tr className="border-b border-border">
                      <td colSpan={9} className="bg-muted/20 px-6 py-4">
                        <ExpandedPaymentDetail payment={payment} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && total > 0 && (
        <DataTablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          onPageChange={setPage}
          perPageOptions={PER_PAGE_OPTIONS}
          onPerPageChange={(n) => {
            setPerPage(n);
            setPage(1);
          }}
        />
      )}
    </div>
  );
}

// ── Expanded Payment Detail ──

function ExpandedPaymentDetail({ payment }: { payment: SPPayment }) {
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold">Payment Details</h4>
      <div className="grid grid-cols-2 gap-2 text-sm max-w-lg">
        <div className="text-muted-foreground">From Account:</div>
        <div>
          {payment.fundingAccount?.nickname} ({payment.fundingAccount?.bankName})
        </div>
        <div className="text-muted-foreground">Payee:</div>
        <div>{payment.payee?.name}</div>
        <div className="text-muted-foreground">Amount:</div>
        <div className="font-medium">
          {formatCurrency(payment.amount)} {payment.currency}
        </div>
        <div className="text-muted-foreground">Method:</div>
        <div className="uppercase">{payment.paymentMethod}</div>
        {payment.statementDescriptor && (
          <>
            <div className="text-muted-foreground">Statement Descriptor:</div>
            <div>{payment.statementDescriptor}</div>
          </>
        )}
        {payment.memo && (
          <>
            <div className="text-muted-foreground">Memo:</div>
            <div>{payment.memo}</div>
          </>
        )}
        {payment.statusMessage && (
          <>
            <div className="text-muted-foreground">Status Note:</div>
            <div className="text-red-600">{payment.statusMessage}</div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Payees Tab ──

function PayeesTab({ onAddPayee }: { onAddPayee: () => void }) {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useSPPayees({ search: search || undefined });
  const payees = data?.data ?? [];
  const deletePayee = useDeleteSPPayee();
  const [editingPayee, setEditingPayee] = useState<SPPayee | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ id: string; name: string } | null>(null);
  const perms = usePermissions();
  const canAddPayee = perms.has('sp.payees.create');
  const canEditPayee = perms.has('sp.payees.edit');
  const canDeletePayee = perms.has('sp.payees.delete');

  const handleConfirmDelete = async () => {
    if (!confirmDialog) return;
    try {
      const result: any = await deletePayee.mutateAsync(confirmDialog.id);
      const refs = result?.references;
      const total = refs ? (refs.po_count || 0) + (refs.product_count || 0) + (refs.payment_count || 0) : 0;
      if (total > 0) {
        const parts: string[] = [];
        if (refs?.po_count) parts.push(`${refs.po_count} purchase order${refs.po_count === 1 ? '' : 's'}`);
        if (refs?.product_count) parts.push(`${refs.product_count} product${refs.product_count === 1 ? '' : 's'}`);
        if (refs?.payment_count) parts.push(`${refs.payment_count} payment${refs.payment_count === 1 ? '' : 's'}`);
        toast.success(`Vendor "${confirmDialog.name}" deactivated. History kept (${parts.join(', ')}).`);
      } else {
        toast.success(`Vendor "${confirmDialog.name}" deactivated.`);
      }
      setConfirmDialog(null);
    } catch (err: any) {
      // Surface the API's clear "VENDOR_IN_USE" message instead of a generic toast.
      const message = err?.error?.message || err?.message || 'Failed to delete vendor';
      toast.error(message);
      setConfirmDialog(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search Payees / Vendors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
        {canAddPayee && (
          <button
            onClick={onAddPayee}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Add Payee
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading payees...</div>
      ) : payees.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-border">
          <EmptyState icon={Users} title="No payees yet" description="Add a payee to start sending payments." />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {payees.map((payee) => (
            <div
              key={payee.id}
              className="rounded-lg border border-border bg-white p-5 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground">{payee.name}</h3>
                  {payee.company && <p className="mt-0.5 text-xs text-muted-foreground">{payee.company}</p>}
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <StatusBadge status={payee.isActive ? 'active' : 'inactive'} />
                  {(canEditPayee || canDeletePayee) && (
                    <div className="relative">
                      <button
                        onClick={() => setOpenMenuId(openMenuId === payee.id ? null : payee.id)}
                        aria-label="Payee actions"
                        title="Payee actions"
                        className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                      {openMenuId === payee.id && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setOpenMenuId(null)} />
                          <div className="absolute right-0 top-8 z-50 w-36 rounded-lg border border-border bg-white py-1 shadow-lg">
                            {canEditPayee && (
                              <button
                                onClick={() => {
                                  setEditingPayee(payee);
                                  setOpenMenuId(null);
                                }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                Edit
                              </button>
                            )}
                            {canDeletePayee && (
                              <button
                                onClick={() => {
                                  setConfirmDialog({ id: payee.id, name: payee.name });
                                  setOpenMenuId(null);
                                }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Delete
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-3 space-y-1.5">
                {payee.email && <p className="text-xs text-muted-foreground">{payee.email}</p>}
                {payee.phone && <p className="text-xs text-muted-foreground">{payee.phone}</p>}
                {payee.bankName && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Building2 className="h-3 w-3" />
                    {payee.bankName}
                    {payee.accountNumber && <span>&middot; {maskAccountNumber(payee.accountNumber)}</span>}
                  </div>
                )}
              </div>

              {payee.paymentCount != null && (
                <div className="mt-3 border-t border-border pt-3">
                  <p className="text-xs text-muted-foreground">
                    {payee.paymentCount} payment{payee.paymentCount !== 1 ? 's' : ''}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit Payee Modal */}
      {editingPayee && <EditPayeeModal payee={editingPayee} onClose={() => setEditingPayee(null)} />}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!confirmDialog}
        title="Delete Payee"
        message={`Are you sure you want to delete "${confirmDialog?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deletePayee.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
}

// ── Edit Payee Modal ──

function EditPayeeModal({ payee, onClose }: { payee: SPPayee; onClose: () => void }) {
  const updatePayee = useUpdateSPPayee();
  // Split existing name into first/last for editing
  const nameParts = (payee.name || '').trim().split(/\s+/);
  const [formData, setFormData] = useState({
    firstName: nameParts[0] || '',
    lastName: nameParts.slice(1).join(' ') || '',
    email: payee.email || '',
    phone: payee.phone || '',
    company: payee.company || '',
    bankName: payee.bankName || '',
    accountHolderName: payee.accountHolderName || '',
    accountBusinessType: payee.accountBusinessType || 'business',
    accountType: payee.accountType || 'checking',
    routingNumber: payee.routingNumber || '',
    accountNumber: payee.accountNumber || '',
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const update = (field: string, value: string) => setFormData((prev) => ({ ...prev, [field]: value }));
  const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

  const handleAlphaInput = (field: string, value: string) => {
    const filtered = value.replace(/[^a-zA-Z\s]/g, '');
    update(field, filtered);
  };

  const handleNumericInput = (field: string, value: string) => {
    const filtered = value.replace(/[^0-9]/g, '');
    update(field, filtered);
  };

  const fieldErrors = useMemo<Record<string, string>>(() => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName)) {
      errors.firstName = 'Only alphabets and spaces are allowed';
    } else if (formData.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName)) {
      errors.lastName = 'Only alphabets and spaces are allowed';
    } else if (formData.lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    }

    // Email: mandatory, valid format
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email address';
    }

    {
      const phoneErr = validatePhone(formData.phone);
      if (phoneErr) errors.phone = phoneErr;
    }

    // Company: required, min 2 chars
    if (!formData.company.trim()) {
      errors.company = 'Company name is required';
    } else if (formData.company.trim().length < 2) {
      errors.company = 'Company name must be at least 2 characters';
    }

    if (formData.bankName.trim() && !/^[a-zA-Z\s]+$/.test(formData.bankName)) {
      errors.bankName = 'Only alphabets and spaces are allowed';
    }

    if (formData.accountHolderName.trim() && !/^[a-zA-Z\s]+$/.test(formData.accountHolderName)) {
      errors.accountHolderName = 'Only alphabets and spaces are allowed';
    }

    // Routing Number: required, numbers only, 9 digits
    if (!formData.routingNumber) {
      errors.routingNumber = 'Routing number is required';
    } else if (!/^[0-9]+$/.test(formData.routingNumber)) {
      errors.routingNumber = 'Only numbers are allowed';
    } else if (formData.routingNumber.length !== 9) {
      errors.routingNumber = 'Routing number must be 9 digits';
    }

    // Account Number: required, numbers only, 6-17 digits
    if (!formData.accountNumber) {
      errors.accountNumber = 'Account number is required';
    } else if (!/^[0-9]+$/.test(formData.accountNumber)) {
      errors.accountNumber = 'Only numbers are allowed';
    } else if (formData.accountNumber.length < 6) {
      errors.accountNumber = 'Account number must be at least 6 digits';
    } else if (formData.accountNumber.length > 17) {
      errors.accountNumber = 'Account number must not exceed 17 digits';
    }

    return errors;
  }, [formData]);

  const isFormValid = Object.keys(fieldErrors).length === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const allTouched: Record<string, boolean> = {};
    Object.keys(formData).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    if (!isFormValid) return;

    try {
      await updatePayee.mutateAsync({
        id: payee.id,
        name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        email: formData.email.trim() || null,
        phone: formData.phone || null,
        company: formData.company.trim() || null,
        bankName: formData.bankName.trim() || null,
        accountHolderName: formData.accountHolderName.trim() || null,
        accountBusinessType: formData.accountBusinessType || null,
        accountType: formData.accountType || null,
        routingNumber: formData.routingNumber || null,
        accountNumber: formData.accountNumber || null,
      });
      toast.success('Payee updated successfully');
      onClose();
    } catch {
      toast.error('Failed to update payee');
    }
  };

  return (
    <ModalOverlay title="Edit Payee" onClose={onClose}>
      {/* PO summary panel — surfaces purchasing activity inside Send Payments
          so AP/finance staff don't need to bounce to POS Management. */}
      <VendorPurchaseOrdersPanel payeeId={payee.id} />

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              value={formData.firstName}
              onChange={(e) => handleAlphaInput('firstName', e.target.value)}
              onBlur={() => markTouched('firstName')}
              placeholder="John"
              className={`mt-1 ${pfc(touched.firstName ? fieldErrors : {}, 'firstName')}`}
              aria-invalid={!!fieldErrors.firstName}
            />
            {touched.firstName && <PayeeFieldError message={fieldErrors.firstName} />}
          </div>
          <div>
            <label className="block text-sm font-medium">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              value={formData.lastName}
              onChange={(e) => handleAlphaInput('lastName', e.target.value)}
              onBlur={() => markTouched('lastName')}
              placeholder="Smith"
              className={`mt-1 ${pfc(touched.lastName ? fieldErrors : {}, 'lastName')}`}
              aria-invalid={!!fieldErrors.lastName}
            />
            {touched.lastName && <PayeeFieldError message={fieldErrors.lastName} />}
          </div>
        </div>

        {/* Contact Fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => update('email', e.target.value)}
              onBlur={() => markTouched('email')}
              placeholder="john@example.com"
              className={`mt-1 ${pfc(touched.email ? fieldErrors : {}, 'email')}`}
              aria-invalid={!!fieldErrors.email}
            />
            {touched.email && <PayeeFieldError message={fieldErrors.email} />}
          </div>
          <div>
            <label className="block text-sm font-medium">Phone</label>
            <PhoneInput
              value={formData.phone}
              onValueChange={(digits) => update('phone', digits)}
              onBlur={() => markTouched('phone')}
              placeholder="123-456-7890"
              className={`mt-1 ${pfc(touched.phone ? fieldErrors : {}, 'phone')}`}
              aria-invalid={!!fieldErrors.phone}
            />
            {touched.phone && <PayeeFieldError message={fieldErrors.phone} />}
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium">
              Company <span className="text-red-500">*</span>
            </label>
            <input
              value={formData.company}
              onChange={(e) => update('company', e.target.value)}
              onBlur={() => markTouched('company')}
              placeholder="Company name"
              className={`mt-1 ${pfc(touched.company ? fieldErrors : {}, 'company')}`}
              aria-invalid={!!fieldErrors.company}
            />
            {touched.company && fieldErrors.company ? (
              <PayeeFieldError message={fieldErrors.company} />
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">If not a company, enter your First Name.</p>
            )}
          </div>
        </div>

        {/* Bank Details Section */}
        <div className="border-t border-border pt-4">
          <h4 className="mb-3 text-sm font-semibold text-foreground">Bank Details</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">Bank Name</label>
              <input
                value={formData.bankName}
                onChange={(e) => handleAlphaInput('bankName', e.target.value)}
                onBlur={() => markTouched('bankName')}
                placeholder="Bank of America"
                className={`mt-1 ${pfc(touched.bankName ? fieldErrors : {}, 'bankName')}`}
                aria-invalid={!!fieldErrors.bankName}
              />
              {touched.bankName && <PayeeFieldError message={fieldErrors.bankName} />}
            </div>
            <div>
              <label className="block text-sm font-medium">Account Holder Name</label>
              <input
                value={formData.accountHolderName}
                onChange={(e) => handleAlphaInput('accountHolderName', e.target.value)}
                onBlur={() => markTouched('accountHolderName')}
                placeholder="John Smith"
                className={`mt-1 ${pfc(touched.accountHolderName ? fieldErrors : {}, 'accountHolderName')}`}
                aria-invalid={!!fieldErrors.accountHolderName}
              />
              {touched.accountHolderName && <PayeeFieldError message={fieldErrors.accountHolderName} />}
            </div>
            <div>
              <label className="block text-sm font-medium">Business / Personal</label>
              <select
                value={formData.accountBusinessType}
                onChange={(e) => update('accountBusinessType', e.target.value)}
                className={`mt-1 ${payeeInputNormal}`}
              >
                <option value="business">Business</option>
                <option value="personal">Personal</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium">Account Type</label>
              <select
                value={formData.accountType}
                onChange={(e) => update('accountType', e.target.value)}
                className={`mt-1 ${payeeInputNormal}`}
              >
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium">
                Routing Number <span className="text-red-500">*</span>
              </label>
              <input
                value={formData.routingNumber}
                onChange={(e) => handleNumericInput('routingNumber', e.target.value)}
                onBlur={() => markTouched('routingNumber')}
                placeholder="123456789"
                maxLength={9}
                className={`mt-1 ${pfc(touched.routingNumber ? fieldErrors : {}, 'routingNumber')}`}
                aria-invalid={!!fieldErrors.routingNumber}
              />
              {touched.routingNumber && <PayeeFieldError message={fieldErrors.routingNumber} />}
            </div>
            <div>
              <label className="block text-sm font-medium">
                Account Number <span className="text-red-500">*</span>
              </label>
              <input
                value={formData.accountNumber}
                onChange={(e) => handleNumericInput('accountNumber', e.target.value)}
                onBlur={() => markTouched('accountNumber')}
                placeholder="1234567890"
                maxLength={17}
                className={`mt-1 ${pfc(touched.accountNumber ? fieldErrors : {}, 'accountNumber')}`}
                aria-invalid={!!fieldErrors.accountNumber}
              />
              {touched.accountNumber && <PayeeFieldError message={fieldErrors.accountNumber} />}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid || updatePayee.isPending}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {updatePayee.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Funding Accounts Tab ──

function FundingAccountsTab() {
  const { data, isLoading } = useSPFundingAccounts();
  const accounts = data?.data ?? [];

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Your source bank account for making payments. It is set from your onboarding bank details and can&apos;t be
        changed here.
      </p>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading accounts...</div>
      ) : accounts.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-border">
          <EmptyState
            icon={Landmark}
            title="No funding account yet"
            description="Your onboarding bank account will appear here. Contact support if it is missing."
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {accounts.map((account) => (
            <div
              key={account.id}
              className="rounded-lg border border-border bg-white p-5 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-foreground truncate">{account.nickname}</h3>
                    <p className="text-xs text-muted-foreground truncate">{account.bankName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  {account.isPrimary && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                      <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                      Primary
                    </span>
                  )}
                  <StatusBadge status={account.isActive ? 'active' : 'inactive'} />
                </div>
              </div>

              <div className="mt-4 space-y-1.5 rounded-md bg-muted/50 px-3 py-2">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="capitalize">{account.accountType}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Account:</span>
                  <span>{maskAccountNumber(account.accountNumber)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Holder:</span>
                  <span>{account.accountHolderName}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Bill Link Dropdown (in Send Payment form) ──

function BillLinkDropdown({
  payeeId,
  onSelect,
}: {
  payeeId: string;
  onSelect: (amount: number, invoiceNumber: string) => void;
}) {
  const { data: billsData } = useVendorInvoiceBills({ payeeId });
  const bills = (billsData?.data ?? []).filter((b) => b.status === 'approved');

  if (bills.length === 0) return null;

  return (
    <div className="mt-2">
      <label className="block text-xs font-medium text-muted-foreground mb-1">Link to Bill (optional)</label>
      <select
        onChange={(e) => {
          const bill = bills.find((b) => b.id === e.target.value);
          if (bill) onSelect(Number(bill.totalAmount), bill.invoiceNumber || bill.id.slice(0, 8));
        }}
        className="h-8 w-full rounded-lg border border-input px-2 text-xs"
        defaultValue=""
      >
        <option value="">No bill selected</option>
        {bills.map((bill) => (
          <option key={bill.id} value={bill.id}>
            #{bill.invoiceNumber || bill.id.slice(0, 8)} — {formatCurrency(bill.totalAmount)}
          </option>
        ))}
      </select>
    </div>
  );
}

// ── Modal Overlay ──

function ModalOverlay({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 pt-[10vh]">
      <div className="relative w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-muted">
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

// ── New Payment Modal ──

// NewPaymentModal removed — replaced by inline SendPaymentTab above

// ── Payee Form Utilities (matching new-sale-tab validation patterns) ──

const payeeInputBase = 'h-9 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:ring-1';
const payeeInputNormal = `${payeeInputBase} border-input focus:border-primary focus:ring-primary`;
const payeeInputError = `${payeeInputBase} border-red-500 focus:border-red-500 focus:ring-red-500`;

function PayeeFieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1 text-xs text-red-500" role="alert">
      {message}
    </p>
  );
}

function pfc(fieldErrors: Record<string, string>, field: string): string {
  return fieldErrors[field] ? payeeInputError : payeeInputNormal;
}

// ── Vendor Purchase Orders Panel ──
// Read-only summary of POs for a vendor, surfaced inside Send Payments so
// AP staff have visibility into purchasing without bouncing apps.

function VendorPurchaseOrdersPanel({ payeeId }: { payeeId: string }) {
  const { data, isLoading } = useVendorPurchaseOrders(payeeId, 5);
  // The full list lives in POS Management, so link there only for those who can open it.
  const canOpenOrders = usePermissions().has('purchase_orders.view');
  const summary = data?.summary;
  const orders = data?.data ?? [];

  if (isLoading) {
    return (
      <div className="mb-4 rounded-lg border border-border bg-muted/20 p-3 text-xs text-muted-foreground">
        Loading purchase orders…
      </div>
    );
  }
  if (!summary || summary.total === 0) {
    return (
      <div className="mb-4 rounded-lg border border-border bg-muted/20 p-3 text-xs text-muted-foreground">
        No purchase orders for this vendor yet.
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-lg border border-border bg-white">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <h4 className="text-sm font-semibold text-foreground">Purchase Orders</h4>
        {canOpenOrders && (
          <a
            href={`#/pos-management?tab=purchase-orders&payeeId=${payeeId}`}
            className="text-xs font-medium text-primary hover:underline"
          >
            View all →
          </a>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 px-3 py-2 text-xs sm:grid-cols-4">
        <div>
          <div className="text-muted-foreground">Drafts</div>
          <div className="font-semibold text-foreground">{summary.drafts}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Sent</div>
          <div className="font-semibold text-foreground">{summary.sent}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Received</div>
          <div className="font-semibold text-foreground">{summary.received}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Outstanding</div>
          <div className="font-semibold text-foreground">{formatCurrency(Number(summary.outstanding) || 0)}</div>
        </div>
      </div>
      {orders.length > 0 && (
        <ul className="divide-y divide-border border-t border-border">
          {orders.map((po) => (
            <li key={po.id} className="flex items-center justify-between px-3 py-2 text-xs">
              <div>
                <span className="font-medium text-primary">#{po.orderNumber}</span>
                <span className="ml-2 text-muted-foreground">
                  {formatDateTime(po.createdAt)} · {po.locationName ?? '—'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">{formatCurrency(Number(po.totalCost) || 0)}</span>
                <StatusBadge status={po.status} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── New Payee Modal ──

function NewPayeeModal({ onClose }: { onClose: () => void }) {
  const createPayee = useCreateSPPayee();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    bankName: '',
    accountHolderName: '',
    accountBusinessType: 'business',
    accountType: 'checking',
    routingNumber: '',
    accountNumber: '',
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [duplicate, setDuplicate] = useState<{
    id: string;
    name: string;
    email: string;
    company: string | null;
  } | null>(null);

  const update = (field: string, value: string) => setFormData((prev) => ({ ...prev, [field]: value }));
  const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

  // Alpha-only filter: allow only letters and spaces
  const handleAlphaInput = (field: string, value: string) => {
    const filtered = value.replace(/[^a-zA-Z\s]/g, '');
    update(field, filtered);
  };

  // Numeric-only filter: allow only digits
  const handleNumericInput = (field: string, value: string) => {
    const filtered = value.replace(/[^0-9]/g, '');
    update(field, filtered);
  };

  // Validation logic (matching new-sale-tab useMemo pattern)
  const fieldErrors = useMemo<Record<string, string>>(() => {
    const errors: Record<string, string> = {};

    // First Name: required, alphabets and spaces only, min 2 chars
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName)) {
      errors.firstName = 'Only alphabets and spaces are allowed';
    } else if (formData.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }

    // Last Name: required, alphabets and spaces only, min 2 chars
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName)) {
      errors.lastName = 'Only alphabets and spaces are allowed';
    } else if (formData.lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    }

    // Email: mandatory, valid format
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email address';
    }

    // Phone: optional, but validate format if provided
    {
      const phoneErr = validatePhone(formData.phone);
      if (phoneErr) errors.phone = phoneErr;
    }

    // Company: required, min 2 chars
    if (!formData.company.trim()) {
      errors.company = 'Company name is required';
    } else if (formData.company.trim().length < 2) {
      errors.company = 'Company name must be at least 2 characters';
    }

    // Bank Name: optional, alphabets and spaces only if provided
    if (formData.bankName.trim() && !/^[a-zA-Z\s]+$/.test(formData.bankName)) {
      errors.bankName = 'Only alphabets and spaces are allowed';
    }

    // Account Holder Name: optional, alphabets and spaces only if provided
    if (formData.accountHolderName.trim() && !/^[a-zA-Z\s]+$/.test(formData.accountHolderName)) {
      errors.accountHolderName = 'Only alphabets and spaces are allowed';
    }

    // Routing Number: required, numbers only, 9 digits
    if (!formData.routingNumber) {
      errors.routingNumber = 'Routing number is required';
    } else if (!/^[0-9]+$/.test(formData.routingNumber)) {
      errors.routingNumber = 'Only numbers are allowed';
    } else if (formData.routingNumber.length !== 9) {
      errors.routingNumber = 'Routing number must be 9 digits';
    }

    // Account Number: required, numbers only, 6-17 digits
    if (!formData.accountNumber) {
      errors.accountNumber = 'Account number is required';
    } else if (!/^[0-9]+$/.test(formData.accountNumber)) {
      errors.accountNumber = 'Only numbers are allowed';
    } else if (formData.accountNumber.length < 6) {
      errors.accountNumber = 'Account number must be at least 6 digits';
    } else if (formData.accountNumber.length > 17) {
      errors.accountNumber = 'Account number must not exceed 17 digits';
    }

    return errors;
  }, [formData]);

  const isFormValid = Object.keys(fieldErrors).length === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Mark all fields as touched
    const allTouched: Record<string, boolean> = {};
    Object.keys(formData).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    if (!isFormValid) return;

    setDuplicate(null);
    try {
      await createPayee.mutateAsync({
        name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        email: formData.email.trim() || null,
        phone: formData.phone || null,
        company: formData.company.trim() || null,
        bankName: formData.bankName.trim() || null,
        accountHolderName: formData.accountHolderName.trim() || null,
        accountBusinessType: formData.accountBusinessType || null,
        accountType: formData.accountType || null,
        routingNumber: formData.routingNumber || null,
        accountNumber: formData.accountNumber || null,
      } as any);
      toast.success('Payee created successfully');
      onClose();
    } catch (err: any) {
      // 409 DUPLICATE_VENDOR — surface inline so user can dismiss & switch
      // to the existing payee in the list rather than re-trying with a new
      // email. The dedup check covers both sp_payees AND the dashboard's
      // payees table so vendors created from PO/Product forms also collide.
      const errBody = err?.error;
      if (errBody?.code === 'DUPLICATE_VENDOR' && errBody.existing) {
        setDuplicate(errBody.existing);
      } else {
        toast.error('Failed to create payee');
      }
    }
  };

  return (
    <ModalOverlay title="Add Payee" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              value={formData.firstName}
              onChange={(e) => handleAlphaInput('firstName', e.target.value)}
              onBlur={() => markTouched('firstName')}
              placeholder="John"
              className={`mt-1 ${pfc(touched.firstName ? fieldErrors : {}, 'firstName')}`}
              aria-invalid={!!fieldErrors.firstName}
            />
            {touched.firstName && <PayeeFieldError message={fieldErrors.firstName} />}
          </div>
          <div>
            <label className="block text-sm font-medium">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              value={formData.lastName}
              onChange={(e) => handleAlphaInput('lastName', e.target.value)}
              onBlur={() => markTouched('lastName')}
              placeholder="Smith"
              className={`mt-1 ${pfc(touched.lastName ? fieldErrors : {}, 'lastName')}`}
              aria-invalid={!!fieldErrors.lastName}
            />
            {touched.lastName && <PayeeFieldError message={fieldErrors.lastName} />}
          </div>
        </div>

        {/* Contact Fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => update('email', e.target.value)}
              onBlur={() => markTouched('email')}
              placeholder="john@example.com"
              className={`mt-1 ${pfc(touched.email ? fieldErrors : {}, 'email')}`}
              aria-invalid={!!fieldErrors.email}
            />
            {touched.email && <PayeeFieldError message={fieldErrors.email} />}
          </div>
          <div>
            <label className="block text-sm font-medium">Phone</label>
            <PhoneInput
              value={formData.phone}
              onValueChange={(digits) => update('phone', digits)}
              onBlur={() => markTouched('phone')}
              placeholder="123-456-7890"
              className={`mt-1 ${pfc(touched.phone ? fieldErrors : {}, 'phone')}`}
              aria-invalid={!!fieldErrors.phone}
            />
            {touched.phone && <PayeeFieldError message={fieldErrors.phone} />}
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium">
              Company <span className="text-red-500">*</span>
            </label>
            <input
              value={formData.company}
              onChange={(e) => update('company', e.target.value)}
              onBlur={() => markTouched('company')}
              placeholder="Company name"
              className={`mt-1 ${pfc(touched.company ? fieldErrors : {}, 'company')}`}
              aria-invalid={!!fieldErrors.company}
            />
            {touched.company && fieldErrors.company ? (
              <PayeeFieldError message={fieldErrors.company} />
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">If not a company, enter your First Name.</p>
            )}
          </div>
        </div>

        {/* Bank Details Section */}
        <div className="border-t border-border pt-4">
          <h4 className="mb-3 text-sm font-semibold text-foreground">Bank Details</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">Bank Name</label>
              <input
                value={formData.bankName}
                onChange={(e) => handleAlphaInput('bankName', e.target.value)}
                onBlur={() => markTouched('bankName')}
                placeholder="Bank of America"
                className={`mt-1 ${pfc(touched.bankName ? fieldErrors : {}, 'bankName')}`}
                aria-invalid={!!fieldErrors.bankName}
              />
              {touched.bankName && <PayeeFieldError message={fieldErrors.bankName} />}
            </div>
            <div>
              <label className="block text-sm font-medium">Account Holder Name</label>
              <input
                value={formData.accountHolderName}
                onChange={(e) => handleAlphaInput('accountHolderName', e.target.value)}
                onBlur={() => markTouched('accountHolderName')}
                placeholder="John Smith"
                className={`mt-1 ${pfc(touched.accountHolderName ? fieldErrors : {}, 'accountHolderName')}`}
                aria-invalid={!!fieldErrors.accountHolderName}
              />
              {touched.accountHolderName && <PayeeFieldError message={fieldErrors.accountHolderName} />}
            </div>
            <div>
              <label className="block text-sm font-medium">Business / Personal</label>
              <select
                value={formData.accountBusinessType}
                onChange={(e) => update('accountBusinessType', e.target.value)}
                className={`mt-1 ${payeeInputNormal}`}
              >
                <option value="business">Business</option>
                <option value="personal">Personal</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium">Account Type</label>
              <select
                value={formData.accountType}
                onChange={(e) => update('accountType', e.target.value)}
                className={`mt-1 ${payeeInputNormal}`}
              >
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium">
                Routing Number <span className="text-red-500">*</span>
              </label>
              <input
                value={formData.routingNumber}
                onChange={(e) => handleNumericInput('routingNumber', e.target.value)}
                onBlur={() => markTouched('routingNumber')}
                placeholder="123456789"
                maxLength={9}
                className={`mt-1 ${pfc(touched.routingNumber ? fieldErrors : {}, 'routingNumber')}`}
                aria-invalid={!!fieldErrors.routingNumber}
              />
              {touched.routingNumber && <PayeeFieldError message={fieldErrors.routingNumber} />}
            </div>
            <div>
              <label className="block text-sm font-medium">
                Account Number <span className="text-red-500">*</span>
              </label>
              <input
                value={formData.accountNumber}
                onChange={(e) => handleNumericInput('accountNumber', e.target.value)}
                onBlur={() => markTouched('accountNumber')}
                placeholder="1234567890"
                maxLength={17}
                className={`mt-1 ${pfc(touched.accountNumber ? fieldErrors : {}, 'accountNumber')}`}
                aria-invalid={!!fieldErrors.accountNumber}
              />
              {touched.accountNumber && <PayeeFieldError message={fieldErrors.accountNumber} />}
            </div>
          </div>
        </div>

        {duplicate && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
            <p className="text-sm font-medium text-amber-900">A vendor with this email already exists</p>
            <p className="mt-1 text-xs text-amber-800">
              <span className="font-semibold">{duplicate.name}</span>
              {duplicate.company ? ` (${duplicate.company})` : ''} · {duplicate.email}
            </p>
            <p className="mt-1 text-xs text-amber-800">
              Close this dialog and find the existing record in the payees list to edit it.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid || createPayee.isPending}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {createPayee.isPending ? 'Creating...' : 'Create Payee'}
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}
